import React from "react";

// SISI_MARKDOWN_V1 — renderizador leve de markdown para as respostas da Sissi.
// Suporta **negrito**, *itálico*, listas (-, •, *, 1.), subitens por indentação,
// títulos (#) e parágrafos. Não usa dangerouslySetInnerHTML: todo texto vira
// nó de texto do React, então não há risco de injeção de HTML.

function renderItalic(text, keyPrefix) {
  const out = [];
  const re = /\*([^*\n]+?)\*/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<em key={`${keyPrefix}-i${i++}`}>{m[1]}</em>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.map((part) => (typeof part === "string" ? part.replace(/\*+/g, "") : part));
}

function renderInline(text, keyPrefix) {
  const out = [];
  const re = /\*\*([^\n]+?)\*\*/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(...renderItalic(text.slice(last, m.index), `${keyPrefix}-a${i}`));
    out.push(
      <strong key={`${keyPrefix}-b${i}`} className="font-semibold">
        {m[1].replace(/\*+/g, "")}
      </strong>
    );
    last = m.index + m[0].length;
    i++;
  }
  if (last < text.length) out.push(...renderItalic(text.slice(last), `${keyPrefix}-z`));
  return out;
}

const LIST_RE = /^(\s*)([-•*]|\d+[.)])\s+(.*)$/;

export default function SisiMarkdown({ text }) {
  if (typeof text !== "string" || !text.trim()) return text ?? null;

  const blocks = [];
  let list = null;
  const flush = () => {
    if (list) {
      blocks.push(list);
      list = null;
    }
  };

  text
    .replace(/\r\n/g, "\n")
    .split("\n")
    .forEach((raw, idx) => {
      const m = raw.match(LIST_RE);
      if (m) {
        if (!list) list = { type: "list", key: `l${idx}`, items: [] };
        list.items.push({
          key: `li${idx}`,
          level: Math.min(Math.floor(m[1].replace(/\t/g, "  ").length / 2), 3),
          marker: /\d/.test(m[2]) ? m[2] : "•",
          text: m[3],
        });
        return;
      }
      flush();
      if (!raw.trim()) {
        if (blocks.length && blocks[blocks.length - 1].type !== "gap") {
          blocks.push({ type: "gap", key: `g${idx}` });
        }
        return;
      }
      const h = raw.match(/^\s*#{1,6}\s+(.*)$/);
      blocks.push({ type: h ? "h" : "p", key: `p${idx}`, text: h ? h[1] : raw });
    });
  flush();
  while (blocks.length && blocks[blocks.length - 1].type === "gap") blocks.pop();

  return (
    <div className="whitespace-normal break-words">
      {blocks.map((b) => {
        if (b.type === "gap") return <div key={b.key} className="h-2" />;
        if (b.type === "h") {
          return (
            <div key={b.key} className="font-semibold">
              {renderInline(b.text, b.key)}
            </div>
          );
        }
        if (b.type === "p") return <div key={b.key}>{renderInline(b.text, b.key)}</div>;
        return (
          <div key={b.key} className="space-y-0.5">
            {b.items.map((it) => (
              <div key={it.key} className="flex gap-1.5" style={{ paddingLeft: it.level * 12 }}>
                <span className="shrink-0 select-none text-muted-foreground">{it.marker}</span>
                <span className="min-w-0">{renderInline(it.text, it.key)}</span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
